/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import { useTranslation } from "react-i18next";
import { ListItem } from "../models";
import { GatewaySiteProps, UpdateGatewayProps } from "../models/devicelist";
import { AlertonDataResponse, EbiDataResponse, EbiNameplate, ExtractedNiagaraNameplate, HeaderIds, HeaderStrings, LinkedDeviceListProps, NiagaraDataResponse } from "../models/linkedDeviceList";
import { DeviceDetails, FileDetails, LicenseDetailEntry, NamePlateAfterRetrieve, PointCountDomain, Supervisor } from "../models/viewdevice";

import {
  DARK_THEME,
  EBI_FORGE_COGNITION_GATEWAY_TYPE,
  GatewayStatus,
  GATEWAY_TOKEN_STORAGE_NAME,
  IAM_PROJECT_ID_SESSION_TOKEN_STORAGE_NAME,
  licenseDetailLabelsToSkip,
  licenseFeatureValueLocaleKeys,
  LOCALE_CONST
} from "./constants";

import { ebiHeaderData, niagaraHeaderData, lowCostGatewayHeaderData, alertonGatewayHeaderData } from "./staticdata";

export const randomValueGenerator = () => {
  const crypto = window.crypto;
  const array = new Uint32Array(1);

  return crypto?.getRandomValues(array)?.[0] || 0;
};

export const getThemeName = (value?: string) => {
  if (value === DARK_THEME) {
    return "dark";
  } else {
    return "light";
  }
};

export const getAccessToken = async (tokenKey?: string) =>
  sessionStorage.getItem(tokenKey || GATEWAY_TOKEN_STORAGE_NAME) || "";

export const getBearerToken = async (tokenKey?: string) =>
  `Bearer ${await getAccessToken(tokenKey)}`;

export const getStatusColor = (status: string) => {
  switch (status) {
    case GatewayStatus.Active:
      return "green";
    case GatewayStatus.Deactivated:
      return "gray";
    case GatewayStatus.Pending:
      return "blue";
    case GatewayStatus.Error:
      return "red";
    case GatewayStatus.CertificateExpired:
      return "red";
    default:
      return "gray";
  }
};

export const displayStatus = (status: string) => {
  if (status === GatewayStatus.Active) {
    return "active";
  } else if (status === GatewayStatus.Error) {
    return "error";
  } else if (status === GatewayStatus.CertificateExpired) {
    return "certificateExpired";
  } else if (status === GatewayStatus.Pending) {
    return "pending";
  } else if (status === GatewayStatus.Activating) {
    return "activating";
  } else if (status === GatewayStatus.Deactivating) {
    return "deactivating";
  } else if (status === GatewayStatus.Deleting) {
    return "deleting";
  } else {
    return "deactivated";
  }
};

export const getIAMProjectID = () =>
  sessionStorage.getItem(IAM_PROJECT_ID_SESSION_TOKEN_STORAGE_NAME) || "";

export const constructDeviceListURL = (
  limit?: number,
  pageNumber?: number,
  filter?: string,
  filterQuery?: string,
  sortBy?: string,
  sortDir?: 'asc' | 'desc' | undefined
): string => {
  let sortParams = "";
  const params = [
    limit !== undefined ? `limit=${limit}` : "",
    pageNumber !== undefined ? `pageNumber=${pageNumber}` : "",
    filter ? `filter=${filter}` : "",
    filterQuery ? filterQuery : ""
  ]
    .filter((param) => param)
    .join("&");

  if (sortDir) {
    sortParams = `&sortBy=${sortBy}&sortDir=${sortDir.toUpperCase()}`;
  }

  return `/v2/projects/${getIAMProjectID()}/devices?${params}${sortParams}`;
};

export const validateInput = (value: string) => {
  if (value.length > 50) {
    return false;
  }
  return true;
};

// Validation utilities
/**
 * Returns true if value contains no disallowed special characters or script tags.
 */
export const validateSpecialChar = (value: string): boolean => {
  const specialCharPattern = /[!@#$%^&*(),.?":{}|<>\\[\];=+_]/g;
  const scriptTagPattern = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
  return !specialCharPattern.test(value) && !scriptTagPattern.test(value);
};

/**
 * Alphanumeric (single token) validator — letters and digits only, no separators.
 */
export const validateAlphanumeric = (value: string): boolean => {
  return /^[a-zA-Z0-9]+$/.test(value);
};

/**
 * Hardware model validator — allows words separated by single space or hyphen.
 * Examples: "Model-1", "Model 1", "ABC123"
 */
export const validateHardwareModel = (value: string): boolean => {
  return /^[a-zA-Z0-9]+([\s-][a-zA-Z0-9]+)*$/.test(value);
};

/**
 * Device / gateway name validator used in forms — disallow consecutive spaces and percent sign.
 */
export const validateDeviceName = (value: string): boolean => {
  if (!value) return false;
  // disallow double spaces or percent sign
  return !/ {2}|%/.test(value);
};

// Convert UTC string to formatted date
export const convertUTCStringtoFormattedDate = (value: string) => {
  if (value) {
    let splitValue = [];

    if (value.includes("T")) {
      splitValue = value.split("T");
    } else {
      splitValue = value.split(" ");
    }

    const hrTime = splitValue?.[1]?.split(":")?.[0] || "0";
    const minTime = splitValue?.[1]?.split(":")?.[1] || "0";

    const timeValue = Number(hrTime);
    const timeFormat = timeValue >= 12 ? "PM" : "AM";

    const dateValue = splitValue?.[0]?.split("-")?.reverse()?.join("/") || "-";

    return `${dateValue} ${hrTime}:${minTime} ${timeFormat} UTC`;
  } else {
    return "-";
  }
};

export const areListsEqual = (
  list1: ListItem[],
  list2: ListItem[]
): boolean => {
  if (!list1 || !list2) {
    return true;
  }

  if (list1.length !== list2.length) {
    return false;
  }

  const sortedList1 = [...list1].sort((a, b) => a.id.localeCompare(b.id));
  const sortedList2 = [...list2].sort((a, b) => a.id.localeCompare(b.id));

  return sortedList1.every(
    (item, index) =>
      item.id === sortedList2[index].id && item.name === sortedList2[index].name
  );
};

/**
 *  {Param} convertFn: returns translate string
 *  {Param} errorMsg: string
 *  {Param} errorStatus: number
 *  {Return} string | returns error message based on status code
 */
export const gatewayRevokeErrMsg = (convertFn: (val: string) => string, errorMsg?: string, errorStatus?: number): string => {
  const errorMessages: { [key: number]: string } = {
    400: convertFn(LOCALE_CONST.badRequestGWID),
    401: convertFn(LOCALE_CONST.unAuthAccessText),
    403: convertFn(LOCALE_CONST.certAlreadyExpired),
    404: convertFn(LOCALE_CONST.notFoundGWInvalid),
    409: convertFn(LOCALE_CONST.conflictGWAlreadyRevoked),
    500: errorMsg || convertFn(LOCALE_CONST.internalServerErrorText),
    502: convertFn(LOCALE_CONST.badGatewayTryAgainLater),
    503: convertFn(LOCALE_CONST.serviceUnavailTryAgainLater),
    504: convertFn(LOCALE_CONST.gwTimeoutRequestTryAgainLater)
  };

  return errorMessages[errorStatus || 503];
};

/**
 *  {Param} convertFn: returns translate string
 *  {Param} errorMsg: string
 *  {Param} errorStatus: number
 *  {Return} string | returns error message based on status code
 */
export const gatewayErrorStatusMessages = (convertFn: (val: string) => string, errorMsg: string, errorStatus: number | string, errMsgJSON?: { [key: string]: string }) => {
  const errorMessages: { [key: number]: string } = {
    400: convertFn(LOCALE_CONST.badRequest),
    401: convertFn(LOCALE_CONST.unAuthAccessText),
    403: convertFn(LOCALE_CONST.forbidAccessDenied),
    404: convertFn(LOCALE_CONST.notFoundGWInvalid),
    409: convertFn(LOCALE_CONST.clientError_409),
    500: errorMsg,
    502: convertFn(LOCALE_CONST.badGatewayTryAgainLater),
    503: convertFn(LOCALE_CONST.serviceUnavailTryAgainLater),
    504: convertFn(LOCALE_CONST.gwTimeoutRequestTryAgainLater)
  };

  if (errMsgJSON) {
    Object.keys(errMsgJSON).forEach((key) => {
      errorMessages[Number(key)] = errMsgJSON[key];
    });
  }

  return errorMessages[errorStatus] || errorMsg;
};

export const convertHeadersToIdContentMap = (headers: HeaderStrings[]): { [key: string]: string } => {
  return headers.reduce((acc, header) => {
    acc[header.id] = header.content;
    return acc;
  }, {} as { [key: string]: string });
};

export const getDynamicHeaderNames = (gatewayType: string, flags?: any): {} => {
  const headerDataNames: { [key: string]: any } = {
    "EBI": convertHeadersToIdContentMap(ebiHeaderData || []),
    "honeywell-niagara-device": convertHeadersToIdContentMap(niagaraHeaderData || []),
    "honeywell-bms-gateway": convertHeadersToIdContentMap(lowCostGatewayHeaderData || []),
    "alerton-compass": convertHeadersToIdContentMap(alertonGatewayHeaderData || [])
  };
  return headerDataNames[gatewayType] || {};
};

export const getDynamicHeaderData = (gatewayType: string, flags?: any): any[] => {
  const headerDataMap: { [key: string]: any[] } = {
    "EBI": ebiHeaderData || [],
    "honeywell-niagara-device": niagaraHeaderData || [],
    "honeywell-bms-gateway": lowCostGatewayHeaderData || [],
    "alerton-compass": alertonGatewayHeaderData || []
  };
  return headerDataMap[gatewayType] || [];
};

export const extractIds = (headers: HeaderIds[]): string[] => {
  return headers.map(header => header.id);
};

export const getDynamicFilterData = (gatewayType: string, flags?: any): any => {
  const filterDataMap: { [key: string]: any[] } = {
    "EBI": extractIds(ebiHeaderData || []),
    "honeywell-niagara-device": extractIds(niagaraHeaderData || []),
    "honeywell-bms-gateway": extractIds(lowCostGatewayHeaderData || []),
    "alerton-compass": extractIds(alertonGatewayHeaderData || [])
  };
  return filterDataMap[gatewayType] || {};
};


export const interfaceDataMap: { [key: string]: any } = {
  "EBI": {} as EbiNameplate,
  "honeywell-niagara-device": {} as ExtractedNiagaraNameplate,
  "honeywell-bms-gateway": {} as ExtractedNiagaraNameplate,
  "alerton-compass": {} as ExtractedNiagaraNameplate
};

export const getDynamicInterfaceData = (gatewayType: string): any => {
  return interfaceDataMap[gatewayType] || {};
};



export const extractAllEbiNameplates = (response: EbiDataResponse, useNamePropertyInNameplate = false): LinkedDeviceListProps[] => {
  const allDevices: LinkedDeviceListProps[] = [];

  response?.data?.forEach((fileData) => {
    Object.entries(fileData?.details?.nameplates || {}).forEach(([key, nameplate]) => {
      const { name, status, properties } = nameplate || {};

      const resolvedName = useNamePropertyInNameplate ? name : "";

      allDevices.push({
        name: resolvedName || key || "",
        status: status || null,
        "firmware-version": properties?.["firmware-version"] || "",
        protocol: properties?.protocol || "",
        manufacturer: properties?.manufacturer || "",
        model: properties?.model || properties?.["model-name"] || "",
        type: properties?.type || "",
        "firmware-revision": properties?.["firmware-revision"] || "",
        description: properties?.description || "",
        "vendor-name": properties?.["vendor-name"] || "",
        "protocol-revision": properties?.["protocol-revision"] || ""
      });
    });
  });

  return allDevices;
};


export const extractAllNiagaraNameplates = (response: NiagaraDataResponse): LinkedDeviceListProps[] => {
  const allDevices: LinkedDeviceListProps[] = [];
  response?.data?.forEach((fileData) => {
    Object?.values(fileData?.details?.nameplates || {}).forEach((nameplate) => {
      const { name, version, properties } = nameplate || {};
      allDevices.push({
        name: name || "",
        status: nameplate?.status || null,
        "firmware-version": properties?.["firmware-version"] || "",
        protocol: properties?.protocol || "",
        manufacturer: properties?.manufacturer || "",
        model: properties?.model || properties?.["model-name"] || "",
        type: properties?.type || "",
        "firmware-revision": properties?.["firmware-revision"] || "",
        description: properties?.description || "",
        "vendor-name": properties?.["vendor-name"] || "",
        "protocol-revision": properties?.["protocol-revision"] || "",
      });
    });
  });

  return allDevices;
};

export const extractAlertonNameplateValues = (response: AlertonDataResponse): LinkedDeviceListProps[] => {
  const allDevices: LinkedDeviceListProps[] = [];

  response?.data?.forEach((fileData) => {
    Object?.values(fileData?.details?.nameplates || {}).forEach((nameplate) => {
      const { name, version, properties } = nameplate || {};
      allDevices.push({
        name: name || "",
        status: nameplate?.status || null,
        "firmware-version": properties?.["firmware-version"] || "",
        protocol: properties?.protocol || "",
        manufacturer: properties?.manufacturer || "",
        model: properties?.model || properties?.["model-name"] || "",
        type: properties?.type || "",
        "firmware-revision": properties?.["firmware-revision"] || "",
        description: properties?.description || "",
        "vendor-name": properties?.["vendor-name"] || "",
        "protocol-revision": properties?.["protocol-revision"] || "",
      });
    });
  });

  return allDevices;
};


export const extractNameplateValues = (response: FileDetails, useSupervisorNestedProperties: boolean): NamePlateAfterRetrieve[] => {
  const allDevices: NamePlateAfterRetrieve[] = [];

  const details = response?.details;

  Object?.values(details?.nameplates || {}).forEach((nameplate) => {
    const { name, version, properties } = nameplate || {};

    const supervisor = useSupervisorNestedProperties ? properties?.supervisor : undefined;
    const pointDomainCounts = createPointDomainCounts(supervisor?.['point-counts']?.domains);

    // For supervisor counts, use new nested structure, fallback to legacy flat properties
    const supervisorPointCountInUse = supervisor?.["point-counts"]?.["in-use"] || properties?.["supervisor-point-count-in-use"] || "";
    const supervisorPointCountLicensed = supervisor?.["point-counts"]?.licensed || properties?.["supervisor-point-count-licensed"] || "";
    const supervisorVersion = supervisor?.version || properties?.["supervisor-version"] || "";

    allDevices.push({
      type: properties?.type || "",
      model: properties?.model || "",
      serial: properties?.serial || "",
      protocol: properties?.protocol || "",
      description: properties?.description || "",
      "gateway-type": properties?.["gateway-type"] || "",
      manufacturer: properties?.manufacturer || "",
      "system-number": properties?.["system-number"] || "",
      "gateway-version": properties?.["gateway-version"] || "",
      "firmware-version": properties?.["firmware-version"] || "",
      "software-version": properties?.["software-version"] || "",
      "technical-address": properties?.["technical-address"] || "",
      "supervisor-version": supervisorVersion,
      "supervisor-point-count-in-use": supervisorPointCountInUse,
      "supervisor-point-count-licensed": supervisorPointCountLicensed,
      "supervisor-point-domain-counts": pointDomainCounts,
      "gw-host-id": properties?.["gw-host-id"] || "",
      "point-count": properties?.["point-count"] || 0,
      "device-count": properties?.["device-count"] || 0,
      "history-count": properties?.["history-count"] || 0,
      "schedule-count": properties?.["schedule-count"] || 0,
      "cloudId-max-count": properties?.["cloudId-max-count"] || 0,
      "alarm-source-count": properties?.["alarm-source-count"] || 0,
      "connectivity-software-version": properties?.["connectivity-software-version"] || "",
      "model-name": properties?.["model-name"] || "",
      "os-version": properties?.["os-version"] || "",
      "agent-version": properties?.["agent-version"] || "",
      "cloud-platform": properties?.["cloud-platform"] || "",
      "database-version": properties?.["database-version"] || "",
      "dvm-release": properties?.["dvm-release"] || "",
      "cameras-used": properties?.["cameras-used"] ?? (properties?.["license-details"]?.find((entry) => entry.label === "Cameras Used")?.value as number) ?? 0,
      "cameras-licensed": properties?.["cameras-licensed"] ?? (properties?.["license-details"]?.find((entry) => entry.label === "Cameras Licensed")?.value as number) ?? 0,
      ...(properties?.["active-primary-camera-servers"] !== undefined
        ? { "active-primary-camera-servers": properties["active-primary-camera-servers"] }
        : {}),
      "database-server-name": properties?.["database-server-name"] || "",
      ...(properties?.["redundant-server-name"] !== undefined
        ? { "redundant-server-name": properties["redundant-server-name"] }
        : {}),
      "connected-ebi-server": properties?.["connected-ebi-server"] ?? "",
      "license-details": properties?.["license-details"] || []
    });
  });

  return allDevices;
};

const createPointDomainCounts = (pointDomainCounts: PointCountDomain[] = []) =>
  pointDomainCounts.reduce<Record<string, number>>((acc, domain) => {
    if (domain.name) {
      acc[domain.name] = domain["point-count"] || 0;
    }
    return acc;
  }, {});

type TFunction = ReturnType<typeof useTranslation>["t"];

export const formatNameplateData = (t: TFunction, nameplateValues?: NamePlateAfterRetrieve, data?: DeviceDetails, gatewayType?: string) => {
  const commonData = [
    {
      label: t(LOCALE_CONST.name),
      value: data?.name || "-",
      isAlertIconVisible: false
    },
    {
      label: t(LOCALE_CONST.gatewayType),
      value: data?.gatewayType || "-",
      isAlertIconVisible: false
    },
    {
      label: t(LOCALE_CONST.systemGUID),
      value: data?.deviceId || "-",
      isAlertIconVisible: false
    },
    {
      label: t(LOCALE_CONST.registeredOn),
      value: convertUTCStringtoFormattedDate(data?.registrationTime) || "-",
      isAlertIconVisible: false
    },
    {
      label: t(LOCALE_CONST.manufacturer),
      value: nameplateValues?.manufacturer || "-",
      isAlertIconVisible: false
    },
    {
      label: t(LOCALE_CONST.model),
      value: nameplateValues?.model || nameplateValues?.["model-name"] || "-",
      isAlertIconVisible: false
    },
    {
      label: t(LOCALE_CONST.description),
      value: nameplateValues?.description || "-",
      isAlertIconVisible: false
    }
  ];

  if (gatewayType === "EBI") {
    return [
      ...commonData,
      {
        label: t(LOCALE_CONST.hostOSVersion),
        value: nameplateValues?.["software-version"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.systemID),
        value: nameplateValues?.["system-number"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.softwareVersion),
        value: nameplateValues?.["supervisor-version"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.firmwareVersion),
        value: nameplateValues?.["software-version"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.gatewayVersion),
        value: nameplateValues?.["gateway-version"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.hostSerialNumber),
        value: nameplateValues?.serial || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.supervisorUseCount),
        value: nameplateValues?.["supervisor-point-count-in-use"] || "-",
        isAlertIconVisible: false
      },
      ...getNameplateDomainPointCounts(nameplateValues, t),
      {
        label: t(LOCALE_CONST.supervisorLicensedCount),
        value: nameplateValues?.["supervisor-point-count-licensed"] || "-",
        isAlertIconVisible: false
      }
    ];
  } else if (gatewayType === "honeywell-niagara-device") {
    return [
      ...commonData,
      {
        label: t(LOCALE_CONST.systemID),
        value: nameplateValues?.["gw-host-id"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.softwareVersion),
        value: nameplateValues?.["software-version"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.firmwareVersion),
        value: nameplateValues?.["firmware-version"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.gatewayVersion),
        value: nameplateValues?.["connectivity-software-version"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.pointCount),
        value: nameplateValues?.["point-count"] === 0 ? 0 : nameplateValues?.["point-count"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.alarmSourceCount),
        value: nameplateValues?.["alarm-source-count"] === 0 ? 0 : nameplateValues?.["alarm-source-count"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.historyCount),
        value: nameplateValues?.["history-count"] === 0 ? 0 : nameplateValues?.["history-count"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.cloudIdMaxCount),
        value: nameplateValues?.["cloudId-max-count"] === 0 ? 0 : nameplateValues?.["cloudId-max-count"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.scheduleCount),
        value: nameplateValues?.["schedule-count"] === 0 ? 0 : nameplateValues?.["schedule-count"] || "-",
        isAlertIconVisible: false
      }
    ];
  } else if (gatewayType === "honeywell-bms-gateway") {
    return [
      ...commonData,
      {
        label: t(LOCALE_CONST.systemID),
        value: nameplateValues?.["gw-host-id"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.softwareVersion),
        value: nameplateValues?.["software-version"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.firmwareVersion),
        value: nameplateValues?.["firmware-version"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.gatewayVersion),
        value: "NA",
        isAlertIconVisible: false
      }
    ];
  } else if (gatewayType === "alerton-compass") {
    return [
      ...commonData,
      {
        label: t(LOCALE_CONST.systemID),
        value: nameplateValues?.["gw-host-id"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.softwareVersion),
        value: nameplateValues?.["software-version"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.firmwareVersion),
        value: nameplateValues?.["firmware-version"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.gatewayVersion),
        value: "NA",
        isAlertIconVisible: false
      }
    ];
  } else if (gatewayType === "OnGuard") {
      return [
        ...commonData,
        {
          label: t(LOCALE_CONST.serialNumber),
          value: nameplateValues?.["serial"] || "-",
          isAlertIconVisible: false
        },
        {
          label: t(LOCALE_CONST.osVersion),
          value: nameplateValues?.["os-version"] || "-",
          isAlertIconVisible: false
        },
        {
          label: t(LOCALE_CONST.agentVersion),
          value: nameplateValues?.["agent-version"] || "-",
          isAlertIconVisible: false
        },
        {
          label: t(LOCALE_CONST.systemNumber),
          value: nameplateValues?.["system-number"] || "-",
          isAlertIconVisible: false
        },
        {
          label: t(LOCALE_CONST.cloudPlatform),
          value: nameplateValues?.["cloud-platform"] || "-",
          isAlertIconVisible: false
        },
        {
          label: t(LOCALE_CONST.databaseVersion),
          value: nameplateValues?.["database-version"] || "-",
          isAlertIconVisible: false
        },
        {
          label: t(LOCALE_CONST.softwareVersion),
          value: nameplateValues?.["software-version"] || "-",
          isAlertIconVisible: false
        },
        {
          label: t(LOCALE_CONST.technicalAddress),
          value: nameplateValues?.["technical-address"] || "-",
          isAlertIconVisible: false
        }
      ];
  } else if (gatewayType === "DVM") {
    return [
      ...commonData,
      {
        label: t(LOCALE_CONST.systemNumber),
        value: nameplateValues?.["system-number"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.dvmRelease),
        value: nameplateValues?.["dvm-release"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.databaseServerName),
        value: nameplateValues?.["database-server-name"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.redundantDatabaseServerName),
        value: nameplateValues?.["redundant-server-name"] || "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.activePrimaryCameraServers),
        value: nameplateValues?.["active-primary-camera-servers"] ?? "-",
        isAlertIconVisible: false
      },
      {
        label: t(LOCALE_CONST.connectedEbiServer),
        value: nameplateValues?.["connected-ebi-server"] || "-",
        isAlertIconVisible: false
      }
    ];
  }

  return commonData;
};

export const getLicenseFeatureRows = (t: TFunction, licenseDetails?: LicenseDetailEntry[]) => {
  const seenLabels = new Set<string>();

  return (licenseDetails || [])
    .filter(({ label }) => {
      if (licenseDetailLabelsToSkip.includes(label) || seenLabels.has(label)) {
        return false;
      }
      seenLabels.add(label);
      return true;
    })
    .map(({ label, value }) => ({
      label,
      value: licenseFeatureValueLocaleKeys[value as string] ? t(licenseFeatureValueLocaleKeys[value as string]) : value,
      isAlertIconVisible: false
    }));
};

// DVM/CC-only card: cameras fixed rows, then one row per license-details field
export const formatDvmLicenseDetails = (t: TFunction, nameplateValues?: NamePlateAfterRetrieve) => [
  {
    label: t(LOCALE_CONST.camerasUsed),
    value: nameplateValues?.["cameras-used"] || "-",
    isAlertIconVisible: false
  },
  {
    label: t(LOCALE_CONST.camerasLicensed),
    value: nameplateValues?.["cameras-licensed"] || "-",
    isAlertIconVisible: false
  },
  ...getLicenseFeatureRows(t, nameplateValues?.["license-details"])
];

export const formatActiveConnectionsTimestamp = (timestamp: string): string => {
  if (!timestamp) {
    return "-";
  }
  const date = new Date(timestamp);

  const hours = date.getUTCHours().toString().padStart(2, '0'); // Get hours in HH format
  const minutes = date.getUTCMinutes().toString().padStart(2, '0'); // Get minutes in MM format
  const day = date.getUTCDate().toString().padStart(2, '0'); // Get day in DD format
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // Get month in MM format (0-based index)
  const year = date.getUTCFullYear(); // Get year in YYYY format

  return `${hours}:${minutes}; ${day}:${month}:${year}`;

};

export const getTimeDifference = (timestamp: string): string => {
  if (!timestamp) {
    return "-";
  }
  const givenTime = new Date(timestamp).getTime();
  const currentTime = new Date().getTime();

  // Calculate the difference in milliseconds
  const timeDifference = Math.abs(currentTime - givenTime);

  // Convert milliseconds to hours, minutes, and seconds
  const hours = Math.floor(timeDifference / (1000 * 60 * 60));
  const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

  return `${hours}h ${minutes}m ${seconds}s`;
};

export const getRemoteAccess = (jsonData: any): any | null => {
  if (!jsonData || !jsonData.nameplates) {
    return false; // Return null if jsonData or nameplates is undefined
  }

  // Iterate over the nameplates to find the first one with "remote-access"
  for (const key in jsonData.nameplates) {
    const nameplate = jsonData.nameplates[key];
    if (nameplate?.properties?.["remote-access"]) {
      return nameplate.properties["remote-access"];
    }
  }

  return false; // Return null if "remote-access" is not found
};

const getNameplateDomainPointCounts = (nameplateValues: NamePlateAfterRetrieve, t: TFunction) => {
  const domainCounts = nameplateValues?.["supervisor-point-domain-counts"] || {};
  const domainEntries = Object.entries(domainCounts);
  return domainEntries.map(([domainName, count]) => ({
    label: t(LOCALE_CONST.supervisorPointCountByDomain, { domain: domainName }),
    value: count?.toString() || "-",
    isAlertIconVisible: false
  }));
};

export const handleApiError = (error: any, convertFn: (key: string) => string, defaultMessage: string, errorMessages: Record<number, string>): string => {
  if (error && "status" in error) {
    return gatewayErrorStatusMessages(convertFn, defaultMessage, error.status, errorMessages);
  }
  return defaultMessage;
};

export const formatFileSize = (bytes: string | number): string => {
  const size = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;
  
  if (isNaN(size) || size === 0) return '0 Bytes';
  
  const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(size) / Math.log(k));
  
  return `${parseFloat((size / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
};

/**
 * Update payload that links a gateway to a site. Keeps the gateway's existing sites and adds the selected one.
 * Used only by the site-context link flow; the edit-gateway payload is built elsewhere and is untouched.
 */
export const buildLinkGatewayPayload = (
  device: any,
  site: { id: string; name: string },
  customerOrgId: string,
  customerName: string
): UpdateGatewayProps & { organizationName?: string | null } => {
  const existingSites: GatewaySiteProps[] = Array.isArray(device?.site) ? device.site : [];
  const sites = [...existingSites.filter((item) => item?.id !== site.id), { id: site.id, name: site.name }];

  const payload: UpdateGatewayProps & { organizationName?: string | null } = {
    name: device?.name,
    organizationId: customerOrgId,
    organizationName: customerName || device?.organizationName || null,
    site: sites
  };

  // Same fields the edit flow sends for EBI-Platform gateways.
  if (device?.gatewayType === EBI_FORGE_COGNITION_GATEWAY_TYPE) {
    const encryptionKey = device?.publicKey || device?.encryptionKey || null;

    if (device?.connectivity === "Disconnected") {
      payload.hardwareId = device?.hardwareId || null;
      payload.model = device?.model || null;
      payload.gatewayType = device?.gatewayType || null;
    }

    payload.encryptionKey = encryptionKey;
  }

  return payload;
};
